import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";

import { useWallet } from "./context/WalletContext";
import { NexauraProvider, useNexauraData } from "./context/NexauraContext";

import AppShell from "./components/layout/AppShell";
import TopBar from "./components/layout/TopBar";
import WrongNetwork from "./components/wallet/WrongNetwork";
import Loader from "./components/common/Loader";

import Connect from "./screens/Connect";
import Register from "./screens/Register";
import Dashboard from "./screens/Dashboard";
import Boards from "./screens/Boards";
import Team from "./screens/Team";
import Rewards from "./screens/Rewards";
import Admin from "./screens/Admin";

// Frame used before the user is registered (top bar + network overlay, no nav).
function RegisterFrame() {
  const { account, isCorrectNetwork } = useWallet();
  return (
    <div className="app-shell">
      <TopBar />
      <Register />
      {account && !isCorrectNetwork ? <WrongNetwork /> : null}
    </div>
  );
}

function AuthedApp() {
  const { switching } = useWallet();
  const { data, loading } = useNexauraData();

  // Network switch in progress -> branded loader, NOT a page reload.
  if (switching) return <Loader text="Switching network" />;

  // First data load -> branded loader (no flash of the wrong screen).
  if (loading && !data.registered && data.userId === 0) {
    return <Loader text="Loading your account" />;
  }

  // Normal unregistered users get the clean Register frame (no nav).
  if (!data.registered && !data.isAdmin && !data.isOwner) return <RegisterFrame />;

  // Registered users -> dashboard. Unregistered admin/owner -> register page
  // (they can still tap the Admin tab).
  const home = data.registered ? "/dashboard" : "/register";

  return (
    <Routes>
      <Route element={<AppShell />}>
        <Route index element={<Navigate to={home} replace />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/register" element={<Register />} />
        <Route path="/boards" element={<Boards />} />
        <Route path="/team" element={<Team />} />
        <Route path="/rewards" element={<Rewards />} />
        {data.isAdmin ? <Route path="/admin" element={<Admin />} /> : null}
        <Route path="*" element={<Navigate to={home} replace />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  const { account } = useWallet();
  const [booting, setBooting] = useState(true);

  // Brief branded boot splash (also lets eager-connect settle).
  useEffect(() => {
    const t = setTimeout(() => setBooting(false), 600);
    return () => clearTimeout(t);
  }, []);

  if (booting) return <Loader text="Initializing" />;

  if (!account) {
    return (
      <div className="app-shell">
        <Connect />
      </div>
    );
  }

  return (
    <NexauraProvider>
      <AuthedApp />
    </NexauraProvider>
  );
}
